import { CheckCircleOutline } from '@mui/icons-material';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useAuth } from 'hooks/useAuth';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listarMinhasListas } from 'services/adminListas';
import { trackClick, trackCreateListClick, usePageView } from 'utils/analytics';
import { BUILD_PROFILE } from 'utils/buildProfile';
import { getSiteUrl } from 'utils/env';

const TEMPLATE_BUTTONS = [
  { slug: 'casamento', label: 'Casamento' },
  { slug: 'cha-de-bebe', label: 'Chá de bebê' },
  { slug: 'aniversario', label: 'Aniversário' },
  { slug: 'vaquinha', label: 'Vaquinha' },
] as const;

type TemplateSlug = (typeof TEMPLATE_BUTTONS)[number]['slug'];

const HomePage: React.FC = () => {
  usePageView('Home');

  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Usuário logado vê a home normalmente; se já tem lista, mostramos o
  // acesso direto a ela (a troca de modelo fica na edição da lista)
  const [minhaLista, setMinhaLista] = useState<{ id: string; slug: string } | null>(null);

  useEffect(() => {
    if (loading || !user) {
      setMinhaLista(null);
      return;
    }
    let ativo = true;
    void listarMinhasListas()
      .then((listas) => {
        if (ativo) setMinhaLista(listas[0] ? { id: listas[0].id, slug: listas[0].slug } : null);
      })
      .catch(() => {
        if (ativo) setMinhaLista(null);
      });
    return () => {
      ativo = false;
    };
  }, [loading, user]);

  const brandTheme = BUILD_PROFILE.theme;
  const brandCopy = BUILD_PROFILE.copy.home;
  const brandAssets = BUILD_PROFILE.assets;
  const isSingleOptionBuild =
    BUILD_PROFILE.brand === 'chadebebe' || BUILD_PROFILE.brand === 'listacasamento';
  const siteUrl = getSiteUrl();
  const orgNameByBrand = {
    presentear: 'Presentear.Net',
    chadebebe: 'ChaDeBebe.Net',
    listacasamento: 'ListaCasamento.Net',
    chadebb: 'Chadebb.com',
  } as const;
  const serviceByBrand = {
    presentear: 'Plataforma SaaS de listas de presentes online.',
    chadebebe: 'Plataforma SaaS de listas de presentes para chá de bebê.',
    listacasamento: 'Plataforma SaaS de listas de presentes para casamento.',
    chadebb: 'Plataforma SaaS de listas de presentes para chá de bebê.',
  } as const;
  const orgName = orgNameByBrand[BUILD_PROFILE.brand];
  const serviceDescription = serviceByBrand[BUILD_PROFILE.brand];

  const faqItems =
    BUILD_PROFILE.brand === 'chadebebe'
      ? [
          {
            question: 'Tem taxa para criar meu chá de bebê?',
            answer:
              'Não. A plataforma é gratuita e os convidados podem presentear sem tarifa do site.',
          },
          {
            question: 'Como os convidados me enviam os presentes?',
            answer:
              'Cada item gera um pagamento Pix com identificação. Você acompanha tudo em tempo real no painel.',
          },
          {
            question: 'Consigo compartilhar no Instagram e WhatsApp?',
            answer: 'Sim. A lista já vem pronta para mobile e o link pode ser enviado em um toque.',
          },
          {
            question: 'Posso editar depois de publicar?',
            answer: 'Sim. Você pode ajustar itens, mensagens e configurações sempre que quiser.',
          },
        ]
      : [
          {
            question: 'Tem alguma taxa?',
            answer:
              'Nao. A plataforma e 100% gratuita e você recebe via Pix sem tarifas cobradas pelo site. Sem surpresas ou armadilhas.',
          },
          {
            question: 'Como recebo os presentes?',
            answer:
              'Cada presente gera um Pix exclusivo com identificador, nome e mensagem. O valor vai direto para sua chave Pix.',
          },
          {
            question: 'Posso usar para casamento, cha de bebe ou aniversario?',
            answer:
              'Sim. Existem templates para varios eventos e você pode personalizar como quiser.',
          },
          {
            question: 'Como o site monetiza?',
            answer:
              'Monetizamos pela divulgacao de produtos dos parceiros. Nao cobramos nada de você nem dos convidados.',
          },
        ];

  return (
    <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" gap={5}>
      {/* Logo fora dos cards, acima de tudo */}
      <Box
        component="img"
        src={brandAssets.homeLogoSrc}
        alt={brandAssets.homeLogoAlt}
        sx={{
          width: 150,
          maxWidth: '100%',
          height: 'auto',
          mt: { xs: -1.5, md: -1 },
          mb: -3,
          borderRadius: 2,
          backgroundColor: 'rgba(255,255,255,0.82)',
          p: 0.6,
          objectFit: 'contain',
          border: `1px solid ${brandTheme.palette.borderSoft}`,
          display: 'block',
          mx: 'auto',
          boxSizing: 'border-box',
        }}
      />
      <Box
        sx={{
          width: '100%',
          maxWidth: 1060,
          borderRadius: { xs: 3, md: 5 },
          p: 2,
          background: `linear-gradient(145deg, ${brandTheme.palette.heroGradientStart} 0%, ${brandTheme.palette.heroGradientEnd} 100%)`,
          border: `1px solid ${brandTheme.palette.borderSoft}`,
          boxShadow: `0 24px 50px ${brandTheme.palette.heroGlow}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(circle at 18% 15%, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0) 36%), radial-gradient(circle at 84% 82%, rgba(255,252,246,0.54) 0%, rgba(255,252,246,0) 40%)',
          },
        }}
      >
        {isSingleOptionBuild && !minhaLista && (
          <Button
            variant="contained"
            size="large"
            sx={{
              mb: 2,
              display: 'flex',
              mx: 'auto',
              px: { xs: 3.5, md: 4.5 },
              py: { xs: 1.3, md: 1.5 },
              borderRadius: 3,
              fontWeight: 700,
              fontSize: { xs: '1rem', md: '1.08rem' },
              letterSpacing: '0.35px',
              boxShadow: `0 14px 28px ${brandTheme.palette.heroGlow}`,
              transition: 'transform 180ms ease, box-shadow 180ms ease',
              position: 'relative',
              zIndex: 1,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 18px 32px ${brandTheme.palette.heroGlow}`,
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              ...(isSingleOptionBuild && {
                animation: 'ctaPulse 1.8s ease-in-out 30',
                '@keyframes ctaPulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(1.04)',
                  },
                },
              }),
            }}
            onClick={() => {
              trackClick('cta_button_hero');
              trackCreateListClick(
                {
                  source: 'home_hero',
                  brand: BUILD_PROFILE.brand,
                },
                () => navigate('/criar-lista')
              );
            }}
          >
            {brandCopy.ctaPrimary}
          </Button>
        )}
        {!isSingleOptionBuild && (
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '1.7rem', sm: '2.25rem', md: '3.2rem' },
              lineHeight: 1.08,
              letterSpacing: '0.15px',
              maxWidth: 760,
              mx: 'auto',
              mt: { xs: 1, sm: 0 },
              position: 'relative',
              zIndex: 1,
            }}
          >
            {brandCopy.title}
          </Typography>
        )}
        <Typography
          variant="h6"
          color={isSingleOptionBuild ? 'text.primary' : 'text.secondary'}
          paragraph
          sx={{
            maxWidth: isSingleOptionBuild ? 560 : 680,
            mx: 'auto',
            fontSize: isSingleOptionBuild
              ? { xs: '1.02rem', md: '1.18rem' }
              : { xs: '0.92rem', md: '1.2rem' },
            lineHeight: isSingleOptionBuild ? 1.55 : 1.45,
            mt: isSingleOptionBuild ? 0.5 : 0,
            mb: isSingleOptionBuild ? 2.2 : undefined,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {brandCopy.subtitle}
        </Typography>
        {/* Chips de benefícios ocultos no mobile para caber sem scroll */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          flexWrap="wrap"
          useFlexGap
          mb={2.5}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          {brandCopy.quickBenefits.map((benefit) => (
            <Chip
              key={benefit}
              label={benefit}
              icon={<CheckCircleOutline />}
              sx={{
                bgcolor: 'rgba(255,255,255,0.8)',
                border: `1px solid ${brandTheme.palette.borderSoft}`,
                '& .MuiChip-label': { fontWeight: 600 },
                '& .MuiChip-icon': { color: brandTheme.palette.primaryMain },
                position: 'relative',
                zIndex: 1,
              }}
            />
          ))}
        </Stack>
        {minhaLista ? (
          // Quem já tem lista vai direto para ela; troca de modelo fica na edição
          <Box sx={{ position: 'relative', zIndex: 1, mt: { xs: 1.5, sm: 2 } }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => {
                trackClick('acessar_minha_lista_home');
                navigate(`/lista/${minhaLista.slug}`);
              }}
              sx={{
                maxWidth: 460,
                px: { xs: 3.5, md: 4.5 },
                py: { xs: 1.2, md: 1.4 },
                borderRadius: 3,
                fontWeight: 700,
                fontSize: { xs: '1rem', md: '1.08rem' },
                letterSpacing: '0.35px',
                boxShadow: `0 14px 28px ${brandTheme.palette.heroGlow}`,
                transition: 'transform 180ms ease, box-shadow 180ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 18px 32px ${brandTheme.palette.heroGlow}`,
                },
              }}
            >
              Acesse sua lista
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1, fontSize: { xs: '0.78rem', md: '0.85rem' } }}
            >
              Quer mudar o modelo (casamento, chá de bebê, aniversário ou vaquinha)? É só trocar
              na edição da sua lista.
            </Typography>
          </Box>
        ) : !isSingleOptionBuild ? (
          <Box
            display="grid"
            gridTemplateColumns="repeat(2, minmax(0, 1fr))"
            gap={{ xs: 1, sm: 1.25 }}
            mt={{ xs: 1.5, sm: 2 }}
            width="100%"
          >
            {TEMPLATE_BUTTONS.map((item) => {
              return (
                <Button
                  key={item.slug}
                  variant="outlined"
                  size="large"
                  startIcon={
                    <Box
                      component="video"
                      src={brandAssets.templateIconVideoSrc[item.slug as TemplateSlug]}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      sx={{
                        width: { xs: 38, sm: 50 },
                        height: { xs: 38, sm: 50 },
                        objectFit: 'cover',
                        borderRadius: '40%',
                        marginX: { xs: 0.5, sm: 2 },
                      }}
                    />
                  }
                  sx={{
                    py: 0,
                    px: { xs: 0.5, sm: 1 },
                    minHeight: { xs: 50, sm: 58 },
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: { xs: '0.82rem', sm: '0.92rem' },
                    letterSpacing: '0.12px',
                    color: brandTheme.palette.textPrimary,
                    borderColor: 'rgba(140, 117, 85, 0.3)',
                    borderWidth: 1.4,
                    background:
                      'linear-gradient(145deg, rgba(255, 255, 255, 0.88) 0%, rgba(246, 238, 225, 0.75) 100%)',
                    boxShadow: '0 8px 16px rgba(90, 71, 47, 0.08)',
                    transition:
                      'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                    justifyContent: 'center',
                    '&:hover': {
                      borderColor: 'rgba(140, 117, 85, 0.44)',
                      background:
                        'linear-gradient(145deg, rgba(255, 255, 255, 0.94) 0%, rgba(244, 234, 219, 0.82) 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 22px rgba(90, 71, 47, 0.12)',
                    },
                    '& .MuiButton-startIcon': {
                      marginLeft: 0,
                      marginRight: 0,
                      flex: { xs: '0 0 42px', sm: '0 0 56px' },
                      display: 'grid',
                      placeItems: 'center',
                    },
                  }}
                  onClick={() => {
                    trackClick('create_list_button', {
                      templateSlug: item.slug,
                      source: 'home',
                    });
                    trackCreateListClick(
                      {
                        source: 'home_template_button',
                        templateSlug: item.slug,
                        brand: BUILD_PROFILE.brand,
                      },
                      () => navigate(`/criar-lista?template=${encodeURIComponent(item.slug)}`)
                    );
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      minWidth: { xs: 0, sm: 132 },
                      textAlign: { xs: 'center', sm: 'left' },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.label}
                  </Box>
                </Button>
              );
            })}
          </Box>
        ) : null}
      </Box>

      {/* Seção: Ideias de Presentes (segunda jornada da home, mesmo visual do hero) */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 1060,
          mt: { xs: -3, md: -2.5 },
          borderRadius: { xs: 3, md: 5 },
          p: 2,
          textAlign: 'center',
          background: `linear-gradient(145deg, ${brandTheme.palette.heroGradientStart} 0%, ${brandTheme.palette.heroGradientEnd} 100%)`,
          border: `1px solid ${brandTheme.palette.borderSoft}`,
          boxShadow: `0 24px 50px ${brandTheme.palette.heroGlow}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(circle at 18% 15%, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0) 36%), radial-gradient(circle at 84% 82%, rgba(255,252,246,0.54) 0%, rgba(255,252,246,0) 40%)',
          },
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontSize: { xs: '1.45rem', md: '2rem' },
            lineHeight: 1.1,
            fontWeight: 700,
            position: 'relative',
            zIndex: 1,
          }}
        >
          Vai dar um presente?
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            mt: 0.75,
            mb: 1.75,
            fontSize: { xs: '0.92rem', md: '1.05rem' },
            maxWidth: 560,
            mx: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          Explore sugestões prontas para namorada, mãe, pai, amigos, crianças e muito mais.
        </Typography>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => {
            try {
              Sentry.logger.info('click_presentear');
            } catch (err) {
              // ignore
            }
            trackClick('ideias_presentes_cta', { source: 'home_section' });
            navigate('/presentes');
          }}
          sx={{
            maxWidth: 460,
            px: { xs: 3.5, md: 4.5 },
            py: { xs: 1.2, md: 1.4 },
            borderRadius: 3,
            fontWeight: 700,
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            letterSpacing: '0.35px',
            boxShadow: `0 14px 28px ${brandTheme.palette.heroGlow}`,
            transition: 'transform 180ms ease, box-shadow 180ms ease',
            position: 'relative',
            zIndex: 1,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 18px 32px ${brandTheme.palette.heroGlow}`,
            },
          }}
        >
          Ver ideias de presentes
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3.2, md: 4.2 },
          maxWidth: 780,
          width: '100%',
          border: `1px solid ${brandTheme.palette.borderSoft}`,
          borderRadius: 4,
          backgroundColor: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(4px)',
          textAlign: 'left',
        }}
      >
        <Typography variant="h5" gutterBottom>
          Como funciona
        </Typography>
        <Stack spacing={1.2}>
          {[
            'Crie sua lista com template pronto em 1 clique.',
            'Compartilhe o link no WhatsApp, Instagram e grupos da familia.',
            'Receba via Pix com identificador e acompanhe tudo no painel.',
          ].map((step, index) => (
            <Stack key={step} direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  minWidth: 28,
                  minHeight: 28,
                  borderRadius: '50%',
                  bgcolor: brandTheme.palette.surfaceAlt,
                  color: brandTheme.palette.primaryMain,
                  fontWeight: 800,
                  display: 'grid',
                  placeItems: 'center',
                  border: `1px solid ${brandTheme.palette.borderSoft}`,
                }}
              >
                {index + 1}
              </Box>
              <Typography variant="body1" color="text.secondary">
                {step}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Box mt={8} maxWidth={800} width="100%">
        <Typography variant="h5" gutterBottom>
          Perguntas frequentes
        </Typography>
        {faqItems.map((item) => (
          <Box key={item.question} mb={2.5} textAlign="left">
            <Typography variant="subtitle1" gutterBottom>
              {item.question}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.answer}
            </Typography>
          </Box>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 980,
          p: { xs: 2.2, sm: 2.8 },
          border: `1px solid ${brandTheme.palette.borderSoft}`,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.82)',
          backdropFilter: 'blur(4px)',
          textAlign: 'left',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          Informações da operação
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          <strong>Operador responsável:</strong> Cristian Terence dos Santos
          <br />
          <strong>Relação com o website:</strong> Criador, proprietário e operador direto da
          plataforma
          <br />
          <strong>Organização:</strong> {orgName}
          <br />
          <strong>Website:</strong> {siteUrl}
          <br />
          <strong>Serviço prestado:</strong> {serviceDescription}
        </Typography>
      </Paper>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          }),
        }}
      />
    </Box>
  );
};

export default HomePage;
